import React, { useEffect, useState } from 'react';

// MUI
import { Box, InputLabel, Input, Checkbox, TextField, Tooltip, Typography } from '@mui/material';

// Styles
import '../AddCostOfGoodModal/styles.scss';
import OLCBaseModal from '../../BaseModal';
import DatePicker from '~/components/General/OlcDatePicker';
import { CheckedCIcon, ContactInfoCIcon, UncheckedCIcon } from '~/assets/images';
import { dateProtector, parseLocalDateOnly } from '~/utils/date-format';

const EditCostOfGoodModal = ({
  open,
  handleClose,
  initialValues = {},
  onSubmit,
  productType = 'postcards',
  category = 'Postcards',
  loading = false,
}) => {
  // Get field configuration based on product type
  const getFieldsConfig = (productType) => {
    switch (productType) {
      case 'postcards':
        return [
          { key: 'four_by_six_postcard', label: '4x6 Postcard*', placeholder: '$0.00' },
          { key: 'six_by_nine_postcard', label: '6x9 Postcard*', placeholder: '$0.00' },
          { key: 'six_by_eleven_postcard', label: '6x11 Postcard*', placeholder: '$0.00' },
          { key: 'nine_by_twelve_postcard', label: '9x12 Postcard*', placeholder: '$0.00' },
        ];
      case 'letters':
        return [
          { key: 'personal_letter', label: 'Personal Letter*', placeholder: '$0.00' },
          {
            key: 'professional_letter_windowed',
            label: 'Professional Letter (Windowed)*',
            placeholder: '$0.00',
          },
          {
            key: 'professional_letter_non_windowed',
            label: 'Professional Letter (Non-Windowed)*',
            placeholder: '$0.00',
          },
        ];
      case 'real_penned':
        return [
          {
            key: 'real_penned_personal_letter',
            label: 'Real Penned Personal Letter*',
            placeholder: '$0.00',
          },
          {
            key: 'real_penned_professional_letter',
            label: 'Real Penned Professional Letter*',
            placeholder: '$0.00',
          },
        ];
      case 'self_mailers':
        return [
          { key: 'bi_fold_self_mailer', label: 'Bi-Fold Self Mailer*', placeholder: '$0.00' },
          { key: 'tri_fold_self_mailer', label: 'Tri-Fold Self Mailer*', placeholder: '$0.00' },
        ];
      case 'snap_packs':
        return [{ key: 'snap_pack_letter', label: 'Snap Pack Letter*', placeholder: '$0.00' }];
      case 'add_ons':
        return [
          { key: 'google_street_view', label: 'Google Street View*', placeholder: '$0.00' },
          { key: 'rapid_offer_system', label: 'Rapid Offer System*', placeholder: '$0.00' },
          {
            key: 'professional_letter_second_page',
            label: 'Professional Letter (Second Page)*',
            placeholder: '$0.00',
          },
          { key: 'uv_coating', label: 'UV Coating*', placeholder: '$0.00' },
        ];
      default:
        return [];
    }
  };

  const fieldsConfig = getFieldsConfig(productType);

  // Initialize state based on product type
  const initializeValues = () => {
    const initialState = {
      effective_date: null,
      notes: '',
      force_update: false,
    };

    fieldsConfig.forEach((field) => {
      initialState[`${field.key}_cost`] = '';
      initialState[`${field.key}_is_applicable`] = false;
      initialState[`${field.key}_additional_increase`] = '';
    });

    return initialState;
  };

  const [values, setValues] = useState(initializeValues());
  const [errors, setErrors] = useState({});

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);

  // Check if any field is marked as applicable to show additional increase
  const showAdditionalIncrease = fieldsConfig.some((field) => values[`${field.key}_is_applicable`]);

  useEffect(() => {
    if (open && initialValues && Object.keys(initialValues).length > 0) {
      let effectiveDate = null;
      if (initialValues.effective_date) {
        effectiveDate = parseLocalDateOnly(initialValues.effective_date);
      }
      const newValues = {
        effective_date: effectiveDate,
        notes: initialValues.notes ?? '',
        force_update: initialValues.force_update ?? false,
      };

      fieldsConfig.forEach((field) => {
        newValues[`${field.key}_cost`] = initialValues[`${field.key}_cost`] ?? '';
        newValues[`${field.key}_is_applicable`] = !!initialValues[`${field.key}_is_applicable`];
        newValues[`${field.key}_additional_increase`] =
          initialValues[`${field.key}_additional_increase`] ?? '';
      });

      setValues(newValues);
      setErrors({});
    } else if (!open) {
      // Reset form when modal closes
      setValues(initializeValues());
      setErrors({});
    }
  }, [open, initialValues, productType]);

  // Validation function for numeric fields
  const validateNumericField = (value, fieldName) => {
    if (value === '' || value === null || value === undefined) {
      return ''; // Empty is allowed
    }

    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return 'Must be a valid number';
    }

    if (numValue <= 0) {
      return 'Must be greater than 0';
    }

    return '';
  };

  const handleChange = (key) => (e) => {
    const value = e?.target?.type === 'checkbox' ? e.target.checked : e.target.value;

    // If unchecking an is_applicable checkbox, clear the corresponding additional_increase field
    if (e?.target?.type === 'checkbox' && key.endsWith('_is_applicable') && !value) {
      const fieldKey = key.replace('_is_applicable', '');
      const increaseKey = `${fieldKey}_additional_increase`;
      setValues((prev) => ({
        ...prev,
        [key]: value,
        [increaseKey]: '',
      }));
      // Also clear the error for the increase field
      setErrors((prev) => ({ ...prev, [increaseKey]: '' }));
    } else {
      setValues((prev) => ({ ...prev, [key]: value }));
    }

    // Validate numeric fields (cost and additional_increase fields)
    if (key.endsWith('_cost') || key.endsWith('_additional_increase')) {
      const error = validateNumericField(value, key);
      setErrors((prev) => ({ ...prev, [key]: error }));
    }
  };

  const handleDateChange = (date) => {
    if (date) {
      dateProtector(date);
    }
    setValues((prev) => ({ ...prev, effective_date: date }));
  };

  // Check if form has validation errors
  const hasValidationErrors = () => {
    return Object.values(errors).some((error) => error !== '');
  };

  // Check if all required fields have data
  const hasRequiredFields = () => {
    // Check effective date
    if (!values.effective_date) {
      return false;
    }

    // Check all product cost fields (all are required)
    return fieldsConfig.every((field) => {
      const costValue = values[`${field.key}_cost`];
      return costValue !== null && costValue !== undefined && costValue !== '';
    });
  };

  // Disable submit if validation errors OR missing required fields
  const isSubmitDisabled = () => {
    return hasValidationErrors() || !hasRequiredFields();
  };

  const handleSave = () => {
    // Validate all numeric fields before saving
    const newErrors = {};

    Object.keys(values).forEach((key) => {
      if (key.endsWith('_cost') || key.endsWith('_additional_increase')) {
        const error = validateNumericField(values[key], key);
        if (error) {
          newErrors[key] = error;
        }
      }
    });

    setErrors(newErrors);

    // Only proceed if no validation errors
    if (Object.keys(newErrors).length === 0) {
      if (onSubmit) onSubmit(values);
    }
  };

  return (
    <OLCBaseModal
      open={open}
      onClose={handleClose}
      onSubmit={handleSave}
      title={`Edit Cost of Goods - ${category}`}
      subTitle="Update cost and options."
      primaryText="Update"
      width={640}
      disableScrollLock={false}
      paperClassName="costOfGoodModalWrapper"
      loading={loading}
      disablePrimary={isSubmitDisabled()}
    >
      <Box className="costOfGoodModalContent">
        <Box className={`fieldsHeader withIncrease`}>
          <InputLabel className="fieldLabel">Item</InputLabel>
          <Box className="updatePricingInfo">
            <InputLabel className="fieldLabel">Update Product</InputLabel>
            <Box className="pricingWithInfo">
              <InputLabel className="fieldLabel">Pricing</InputLabel>
              <Tooltip
                title={
                  <Typography className="tooltipText">
                    The cost difference will automatically be applied to your pricing. You can add
                    an additional increase if desired.
                  </Typography>
                }
                placement="bottom-start"
              >
                <img src={ContactInfoCIcon} alt="info" style={{ cursor: 'pointer' }} />
              </Tooltip>
            </Box>
          </Box>
          {showAdditionalIncrease && (
            <InputLabel className="fieldLabel">Add'l Increase (optional)</InputLabel>
          )}
        </Box>
        {fieldsConfig.map((field) => (
          <React.Fragment key={field.key}>
            <InputLabel className="rowTopLabel">{field.label}</InputLabel>
            <Box className={`rowNew withIncrease`}>
              <Box className="productCell">
                <Box className="inputWithError">
                  <Input
                    type="number"
                    className={`rowInput ${errors[`${field.key}_cost`] ? 'invalid' : ''}`}
                    placeholder={field.placeholder}
                    value={values[`${field.key}_cost`] || ''}
                    onChange={handleChange(`${field.key}_cost`)}
                  />
                  {errors[`${field.key}_cost`] && (
                    <Typography className="errorMessage">
                      <sup>*</sup>
                      {errors[`${field.key}_cost`]}
                    </Typography>
                  )}
                </Box>
                <Checkbox
                  className="rowCheck returnAddressCheck"
                  checked={values[`${field.key}_is_applicable`] || false}
                  onChange={handleChange(`${field.key}_is_applicable`)}
                  icon={<img src={UncheckedCIcon} alt="unchecked" />}
                  checkedIcon={<img src={CheckedCIcon} alt="checked" />}
                />
                {showAdditionalIncrease && (
                  <Box className="increaseCell">
                    {values[`${field.key}_is_applicable`] ? (
                      <Box className="inputWithError">
                        <Input
                          type="number"
                          className={`rowInput ${errors[`${field.key}_additional_increase`] ? 'invalid' : ''}`}
                          placeholder="Enter additional increase"
                          value={values[`${field.key}_additional_increase`] || ''}
                          onChange={handleChange(`${field.key}_additional_increase`)}
                        />
                        {errors[`${field.key}_additional_increase`] && (
                          <Typography className="errorMessage">
                            <sup>*</sup>
                            {errors[`${field.key}_additional_increase`]}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <span />
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </React.Fragment>
        ))}
        <Box className="divider" />
        <Box className="mailedDate newDatePicker updatedDesign">
          <Box className="selectDate">
            <InputLabel className="dateLabel newfieldsLabel">Effective Date*</InputLabel>
            <DatePicker
              minDate={tomorrow}
              onChange={handleDateChange}
              dateFormat="MM/dd/yyyy"
              className="datePicker"
              placeholderText="Select date"
              selected={values.effective_date}
              onBlur={(e) => {
                if (e.target.value == '') {
                  handleDateChange(null);
                }
              }}
            />
          </Box>
        </Box>
        <Box className="rowTopLabel">Notes</Box>
        <Box className="row">
          <TextField
            className="notesInput"
            multiline
            minRows={2}
            placeholder="Add any notes..."
            value={values.notes.trimStart()}
            onChange={handleChange('notes')}
          />
        </Box>
      </Box>
    </OLCBaseModal>
  );
};

export default EditCostOfGoodModal;
